import { MessageSquare, Plus, Sparkles, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth';
import { useAssistant } from '../components/assistant/AssistantContext';
import { Conversation } from '../components/assistant/Conversation';
import { Empty, PageLoader } from '../components/ui';
import { relativeTime } from '../format';
import { useI18n } from '../i18n';

const SETUP_EXAMPLE = `# Free hosted model (Groq):
ASSISTANT_BASE_URL=https://api.groq.com/openai/v1
ASSISTANT_MODEL=<model id from the provider>
ASSISTANT_API_KEY=<your key>

# Or a local model with Ollama:
ASSISTANT_BASE_URL=http://localhost:11434/v1
ASSISTANT_MODEL=qwen3:8b`;

/** Full-page assistant: saved conversations on the left, the chat on the right. */
export function AssistantPage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const { enabled, off, loading, chats, active, newChat, openChat, deleteChat } = useAssistant();

  if (loading) return <PageLoader />;
  if (off) {
    return (
      <Empty icon={<Sparkles />} title={t('assistant.title')}>
        <p>{t('assistant.turned_off')}</p>
        {can('settings.manage') && (
          <Link to="/admin/settings" className="btn sm">
            {t('nav.settings')}
          </Link>
        )}
      </Empty>
    );
  }
  if (!enabled) {
    return (
      <Empty icon={<Sparkles />} title={t('assistant.title')}>
        <p>{t('assistant.not_enabled')}</p>
        {can('settings.manage') && (
          <div className="assistant-setup">
            <p>{t('assistant.setup_intro')}</p>
            <pre>{SETUP_EXAMPLE}</pre>
            <p>{t('assistant.setup_after')}</p>
          </div>
        )}
      </Empty>
    );
  }

  return (
    <div className="assistant-page">
      <aside className="assistant-chats" aria-label={t('assistant.chats')}>
        <button type="button" className="btn primary" onClick={newChat}>
          <Plus /> {t('assistant.new_chat')}
        </button>
        <div className="assistant-chats-label">{t('assistant.chats')}</div>
        {chats.length === 0 && <p className="assistant-chats-empty">{t('assistant.no_chats')}</p>}
        <ul>
          {chats.map((c) => (
            <li key={c.id} className={c.id === active?.id ? 'active' : ''}>
              <button type="button" className="assistant-chat-link" onClick={() => openChat(c.id)} aria-current={c.id === active?.id ? 'true' : undefined}>
                <MessageSquare aria-hidden />
                <span className="assistant-chat-text">
                  <span className="assistant-chat-title">{c.title}</span>
                  <span className="assistant-chat-time">{relativeTime(new Date(c.updatedAt).toISOString())}</span>
                </span>
              </button>
              <button
                type="button"
                className="icon-btn assistant-chat-delete"
                onClick={() => deleteChat(c.id)}
                aria-label={t('assistant.delete_chat')}
                title={t('assistant.delete_chat')}
              >
                <Trash2 />
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <Conversation variant="page" key={active?.id ?? 'new'} />
    </div>
  );
}
